'use client'

import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { format, addMonths, subMonths, setMonth, setYear } from 'date-fns'
import { cn } from '@/services/utils/cn'
import { buttonVariants } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface MonthYearPickerProps {
  date: Date
  onChange: (date: Date) => void
  className?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
}

export function MonthYearPicker({
  date,
  onChange,
  className,
  disabled,
  minDate,
  maxDate,
}: MonthYearPickerProps) {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  // Generate years
  const currentYear = new Date().getFullYear()
  const yearStart = currentYear - 100
  const yearEnd = currentYear
  const years = Array.from(
    { length: yearEnd - yearStart + 1 },
    (_, i) => yearStart + i
  )

  const handlePreviousMonth = () => {
    onChange(subMonths(date, 1))
  }

  const handleNextMonth = () => {
    onChange(addMonths(date, 1))
  }

  const handleMonthChange = (value: string) => {
    const monthIndex = months.findIndex((m) => m === value)
    if (monthIndex !== -1) {
      onChange(setMonth(date, monthIndex))
    }
  }

  const handleYearChange = (value: string) => {
    onChange(setYear(date, parseInt(value)))
  }

  return (
    <div className={cn('flex flex-col space-y-2', className)}>
      <div className="flex items-center justify-between">
        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'outline', size: 'icon' }),
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
          )}
          onClick={handlePreviousMonth}
          disabled={disabled || (minDate && date <= minDate)}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only">Previous month</span>
        </button>

        <div className="flex items-center space-x-2">
          <Select
            value={format(date, 'MMMM')}
            onValueChange={handleMonthChange}
            disabled={disabled}
          >
            <SelectTrigger className="h-8 w-32">
              <SelectValue placeholder={format(date, 'MMMM')} />
            </SelectTrigger>
            <SelectContent>
              {months.map((month) => (
                <SelectItem key={month} value={month}>
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={date.getFullYear().toString()}
            onValueChange={handleYearChange}
            disabled={disabled}
          >
            <SelectTrigger className="h-8 w-20">
              <SelectValue placeholder={date.getFullYear().toString()} />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <button
          type="button"
          className={cn(
            buttonVariants({ variant: 'outline', size: 'icon' }),
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
          )}
          onClick={handleNextMonth}
          disabled={disabled || (maxDate && date >= maxDate)}
        >
          <ChevronRight className="h-4 w-4" />
          <span className="sr-only">Next month</span>
        </button>
      </div>
    </div>
  )
}

// Enhanced Calendar component that includes the MonthYearPicker
import { DayPicker } from 'react-day-picker'

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  enableMonthYearPicker?: boolean
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  enableMonthYearPicker = true,
  ...props
}: CalendarProps) {
  // Controlled month state for the new picker
  const [month, setMonth] = React.useState<Date>(
    props.defaultMonth || new Date()
  )

  // Update month when DayPicker's month changes
  React.useEffect(() => {
    if (props.month) {
      setMonth(props.month)
    }
  }, [props.month])

  // Custom caption component that uses our MonthYearPicker
  const CustomCaption = ({ displayMonth }: { displayMonth: Date }) => {
    if (!enableMonthYearPicker) {
      return (
        <div className="relative flex items-center justify-center pt-1">
          <span className="text-sm font-medium">
            {format(displayMonth, 'MMMM yyyy')}
          </span>
          <div className="absolute right-1 flex items-center space-x-1">
            {props.components?.IconLeft && <props.components.IconLeft />}
            {props.components?.IconRight && <props.components.IconRight />}
          </div>
        </div>
      )
    }

    return (
      <MonthYearPicker
        date={displayMonth}
        onChange={(date) => {
          setMonth(date)
          if (props.onMonthChange) {
            props.onMonthChange(date)
          }
        }}
        disabled={!!props.disabled}
        minDate={props.fromDate}
        maxDate={props.toDate}
      />
    )
  }

  return (
    <DayPicker
      month={month}
      onMonthChange={setMonth}
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell:
          'text-muted-foreground rounded-full w-8 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-full',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-full [&:has(>.day-range-start)]:rounded-l-full first:[&:has([aria-selected])]:rounded-l-full last:[&:has([aria-selected])]:rounded-r-full'
            : '[&:has([aria-selected])]:rounded-full'
        ),
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 w-8 p-0 font-normal rounded-full aria-selected:opacity-100'
        ),
        day_range_start: 'day-range-start',
        day_range_end: 'day-range-end',
        day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today: 'bg-accent text-accent-foreground',
        day_outside:
          'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ ...props }) => (
          <ChevronLeft className="h-4 w-4" {...props} />
        ),
        IconRight: ({ ...props }) => (
          <ChevronRight className="h-4 w-4" {...props} />
        ),
        Caption: CustomCaption,
        ...props.components,
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
